import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ujmsDailyDigest } from "../../services/ujmsApi";
import { Panel, Stat, Badge, thCls, tdCls, fmtDT, cls } from "../ui";
import { CalendarClock, AlertTriangle, Timer } from "lucide-react";

export default function DailyDigest() {
  const [d, setD] = useState(null);
  useEffect(() => { ujmsDailyDigest().then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-sm text-slate-400 p-4" data-testid="digest-loading">Preparing your digest…</div>;

  return (
    <div className="space-y-5" data-testid="daily-digest">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Daily Digest — {d.date}</h1>
        <p className="text-sm text-slate-500">Personalized to your role ({d.scope}): upcoming hearings, task deadlines and speedy-trial clocks.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Hearings (7 days)" value={d.hearings_count} testId="dg-stat-hearings" />
        <Stat label="Tasks Due Soon" value={d.tasks_count} testId="dg-stat-tasks" />
        <Stat label="Overdue Tasks" value={d.overdue_count} testId="dg-stat-overdue" tone={d.overdue_count ? "warn" : undefined} />
        <Stat label="Speedy-Trial Alerts" value={d.speedy_alerts} testId="dg-stat-speedy" tone={d.speedy_alerts ? "warn" : undefined} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <Panel title="Upcoming hearings" testId="dg-hearings">
          {d.hearings.length === 0 ? (
            <div className="text-sm text-slate-400 p-3">No hearings in the next 7 days.</div>
          ) : (
            <table className="w-full border-collapse"><tbody>
              {d.hearings.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(h.scheduled_start)}</td>
                  <td className={tdCls}>
                    <Link className="text-blue-900 hover:underline font-mono text-xs" to={`/ujms/cases/${h.case_id}`}>{h.case_number}</Link>
                    <div className="text-xs text-slate-500">{h.hearing_type_label || h.hearing_type}</div>
                  </td>
                  <td className={tdCls}><Badge value={h.status} /></td>
                </tr>
              ))}
            </tbody></table>
          )}
        </Panel>

        <Panel title="Tasks due within 3 days" testId="dg-tasks">
          {d.tasks.length === 0 ? (
            <div className="text-sm text-slate-400 p-3">Nothing due — queue is clear.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {d.tasks.map((t) => (
                <div key={t.id} className="px-3 py-2 text-sm flex items-center justify-between gap-2">
                  <div>
                    <div className="text-slate-800">{t.title}</div>
                    <div className={cls("text-xs font-mono", t.overdue ? "text-red-700 font-semibold" : "text-slate-400")}>
                      Due {t.due_date}{t.overdue ? " — OVERDUE" : ""}
                    </div>
                  </div>
                  <Badge value={t.priority} />
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Speedy-trial clocks (fictional 120/160-day demonstration terms)" testId="dg-speedy">
        {d.speedy_trial.length === 0 ? (
          <div className="text-sm text-slate-400 p-3">No open criminal cases in your scope.</div>
        ) : (
          <table className="w-full border-collapse">
            <thead><tr>
              <th className={thCls}>Case</th><th className={thCls}>Custody</th><th className={thCls}>Clock Basis</th>
              <th className={thCls}>Elapsed</th><th className={thCls}>Remaining</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {d.speedy_trial.map((c) => (
                <tr key={c.case_id} className="hover:bg-slate-50" data-testid={`dg-clock-${c.case_number}`}>
                  <td className={`${tdCls} font-mono text-xs`}>
                    <Link className="text-blue-900 hover:underline" to={`/ujms/cases/${c.case_id}`}>{c.case_number}</Link>
                    <div className="text-slate-400 truncate max-w-[240px]">{c.caption}</div>
                  </td>
                  <td className={tdCls}>{c.custody ? <Badge value="IN CUSTODY" /> : <span className="text-xs text-slate-400">No</span>}</td>
                  <td className={`${tdCls} text-xs`}>{c.clock_basis} ({c.clock_start}) · {c.term_days}-day term</td>
                  <td className={`${tdCls} font-mono text-xs`}>{c.days_elapsed}d</td>
                  <td className={tdCls}>
                    <div className="flex items-center gap-2">
                      <div className="w-28 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                        <div className={cls("h-full", c.days_remaining <= 30 ? "bg-red-600" : c.days_remaining <= 60 ? "bg-amber-500" : "bg-emerald-600")}
                          style={{ width: `${Math.min(100, Math.max(2, (c.days_elapsed / c.term_days) * 100))}%` }} />
                      </div>
                      <span className={cls("font-mono text-xs", c.days_remaining <= 30 ? "text-red-700 font-semibold" : "text-slate-600")}>{c.days_remaining}d</span>
                    </div>
                  </td>
                  <td className={tdCls}>
                    {c.alert && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-red-50 border border-red-200 text-red-700 rounded-sm px-1.5 py-0.5" data-testid={`dg-alert-${c.case_number}`}>
                        <Timer className="w-3 h-3" /> Term alert
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <div className="flex items-start gap-2 text-xs text-slate-400">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        Speedy-trial terms shown here are simplified fictional demonstration values, not legal computations. A digest notification is delivered to your bell once per day.
      </div>
    </div>
  );
}
